import { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Typography,
    Box,
    Alert,
    Autocomplete,
    IconButton,
    Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik, Field } from 'formik';
import * as Yup from 'yup';
import { ItemType, queryItemTypesWithLocations, queryStorageLocations, queryEvents } from '../schema';
import { recordInventoryChange } from '../utils/inventoryHistory';
import { calculateItemLocations } from '../utils/inventory';

interface RecordInventoryChangeDialogProps {
    open: boolean;
    onClose: () => void;
    itemType: ItemType | null;
    mode: 'purchase' | 'lostOrBroken' | 'adjustment';
    event?: { id: string; name: string } | null;
    onSuccess?: () => void;
}

interface FormValues {
    quantity: number;
    note: string;
    locationId: string;
    isBroken?: boolean;
}

interface ModeConfig {
    title: string;
    icon: React.ReactNode;
    color: 'success' | 'error' | 'warning' | 'info';
    quantitySign: 'positive' | 'negative' | 'either';
    confirmText: string;
    notePlaceholder?: string;
    showLostBrokenToggle?: boolean;
}

const createValidationSchema = (
    locationData: {
        storageLocations: { id: string; quantity: number }[];
        events: { eventId: string; quantity: number }[];
    },
    isDecreasing: boolean,
    isAdjustment: boolean
) => {
    return Yup.object({
        quantity: isAdjustment
            ? Yup.number().required('Quantity is required').min(0, 'Quantity cannot be negative').integer('Quantity must be a whole number')
            : Yup.number()
                  .required('Quantity is required')
                  .positive('Quantity must be greater than 0')
                  .integer('Quantity must be a whole number')
                  .test('max-available', 'Cannot decrease by more than available at this location', function (value) {
                      if (!isDecreasing || !value) return true;
                      const locationId = this.parent.locationId;
                      const storage = locationData.storageLocations.find(s => s.id === locationId);
                      const eventLoc = locationData.events.find(e => e.eventId === locationId);
                      const availableQuantity = storage?.quantity ?? eventLoc?.quantity ?? 0;
                      return value <= availableQuantity;
                  }),
        note: Yup.string(),
        locationId: Yup.string().required('Location is required'),
    });
};

export default function RecordInventoryChangeDialog({ open, onClose, itemType, mode, event, onSuccess }: RecordInventoryChangeDialogProps) {
    const [error, setError] = useState<string | null>(null);
    const { data: eventsData } = queryEvents();
    const { data: storageLocationsData } = queryStorageLocations();
    const events = eventsData?.events || [];
    const storageLocations = useMemo(
        () => [...(storageLocationsData?.storageLocations ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [storageLocationsData]
    );
    const { data: itemTypesData } = queryItemTypesWithLocations();

    if (!itemType) return null;

    // Get current item type with locations for validation
    const currentItemType = itemTypesData?.itemTypes?.find(it => it.id === itemType.id);
    const itemTypeWithLocations = currentItemType as any;
    const locations =
        itemTypeWithLocations && 'itemLocations' in itemTypeWithLocations
            ? ((itemTypeWithLocations.itemLocations || []) as Array<{
                  id: string;
                  quantity: number;
                  event: { id: string; name: string } | null | undefined;
                  storageLocation?: { id: string; name: string } | null;
              }>)
            : [];
    const locationData = calculateItemLocations(itemType, locations);

    const isAdjustment = mode === 'adjustment';

    // Mode-specific configuration
    const modeConfig: Record<'purchase' | 'lostOrBroken' | 'adjustment', ModeConfig> = {
        purchase: {
            title: 'Purchase',
            icon: <AddIcon />,
            color: 'success',
            quantitySign: 'positive',
            confirmText: 'This will increase inventory',
            notePlaceholder: 'Optional notes about the purchase...',
        },
        lostOrBroken: {
            title: 'Lost / Broken Items',
            icon: <RemoveIcon />,
            color: 'error',
            quantitySign: 'negative',
            confirmText: 'This will decrease inventory',
            notePlaceholder: 'Where/when were items lost or broken?',
            showLostBrokenToggle: true,
        },
        adjustment: {
            title: 'Adjust Quantity',
            icon: <TuneIcon />,
            color: 'info',
            quantitySign: 'either',
            confirmText: 'Set the correct total quantity at this location',
            notePlaceholder: 'Reason for adjustment...',
        },
    };

    const config = modeConfig[mode];
    const isDecreasing = mode === 'lostOrBroken';

    const handleSubmit = async (values: FormValues) => {
        if (!itemType) return;

        const isStorageLocation = locationData.storageLocations.some(s => s.id === values.locationId);
        const isEvent = locationData.events.some(e => e.eventId === values.locationId);
        const eventId = isEvent ? values.locationId : null;
        const storageLocationId = isStorageLocation ? values.locationId : null;

        try {
            setError(null);

            // Find pre-loaded itemLocation for this selection (avoids queryOnce, works offline)
            const existingItemLocation =
                locations.find(loc => loc.event?.id === values.locationId || loc.storageLocation?.id === values.locationId) ?? null;

            if (isAdjustment) {
                // For adjustment, compute delta from current quantity
                const currentQty = getAvailableQuantity(values.locationId);
                const delta = values.quantity - currentQty;
                if (delta === 0) {
                    onClose();
                    return;
                }
                await recordInventoryChange({
                    itemTypeId: itemType.id,
                    type: 'adjustment',
                    quantity: delta,
                    note: values.note || undefined,
                    eventId,
                    storageLocationId,
                    existingItemLocation,
                });
            } else {
                const changeType = mode === 'lostOrBroken' ? (values.isBroken ? 'broken' : 'lost') : mode;
                await recordInventoryChange({
                    itemTypeId: itemType.id,
                    type: changeType as 'purchase' | 'lost' | 'broken',
                    quantity: values.quantity,
                    note: values.note || undefined,
                    eventId,
                    storageLocationId,
                    existingItemLocation,
                });
            }
            onSuccess?.();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to record inventory change');
        }
    };

    const defaultLocationId = storageLocations[0]?.id ?? event?.id ?? '';

    // Helper to get available quantity at selected location
    const getAvailableQuantity = (locationId: string): number => {
        const storage = locationData.storageLocations.find(s => s.id === locationId);
        if (storage) return storage.quantity;
        const eventLocation = locationData.events.find(e => e.eventId === locationId);
        return eventLocation?.quantity || 0;
    };

    const defaultQuantity = isAdjustment ? getAvailableQuantity(event?.id || defaultLocationId) : 1;

    const initialValues: FormValues = {
        quantity: defaultQuantity,
        note: '',
        locationId: event?.id || defaultLocationId,
        ...(mode === 'lostOrBroken' ? { isBroken: false } : {}),
    };

    const validationSchema = createValidationSchema(locationData, isDecreasing, isAdjustment);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: `${config.color}.main`, display: 'flex', alignItems: 'center' }}>{config.icon}</Box>
                    <Typography variant="h6">{config.title}</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                {({ values, handleBlur, isSubmitting, setFieldValue, errors }) => {
                    const availableQuantity = getAvailableQuantity(values.locationId);
                    const exceedsAvailable = isDecreasing && values.quantity > availableQuantity;
                    const adjustmentDelta = isAdjustment ? values.quantity - availableQuantity : 0;
                    const adjustmentUnchanged = isAdjustment && adjustmentDelta === 0;
                    const canSubmit = !isSubmitting && !exceedsAvailable && !adjustmentUnchanged && Object.keys(errors).length === 0;

                    return (
                        <Form>
                            <DialogContent>
                                <Stack spacing={3}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Item: <strong>{itemType.name}</strong>
                                        </Typography>
                                        {event && (
                                            <Typography variant="body2" color="text.secondary">
                                                Event: <strong>{event.name}</strong>
                                            </Typography>
                                        )}
                                    </Box>

                                    <Alert severity={config.color === 'success' ? 'info' : config.color === 'info' ? 'info' : 'warning'}>
                                        {config.confirmText}
                                    </Alert>

                                    {isDecreasing && exceedsAvailable && (
                                        <Alert severity="error">
                                            Cannot decrease by {values.quantity}.{' '}
                                            {availableQuantity === 0 ? 'None available' : `Only ${availableQuantity} available`} at this
                                            location.
                                        </Alert>
                                    )}

                                    {config.showLostBrokenToggle && (
                                        <Field name="isBroken">
                                            {({ field, meta: _meta }: any) => (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <FindInPageIcon fontSize="small" />
                                                        <Typography
                                                            variant="body2"
                                                            color={!field.value ? 'text.primary' : 'text.secondary'}
                                                        >
                                                            Lost
                                                        </Typography>
                                                    </Box>
                                                    <Switch
                                                        checked={!!field.value}
                                                        onChange={e => setFieldValue('isBroken', e.target.checked)}
                                                        color="default"
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <BrokenImageIcon fontSize="small" />
                                                        <Typography variant="body2" color={field.value ? 'text.primary' : 'text.secondary'}>
                                                            Broken
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>
                                    )}

                                    <Field name="locationId">
                                        {({ field, meta }: any) => {
                                            const locationOptions = [
                                                ...storageLocations.map(s => ({ id: s.id, name: s.name })),
                                                ...events.map(ev => ({ id: ev.id, name: ev.name })),
                                            ];
                                            const selectedLocation = locationOptions.find(opt => opt.id === field.value) || null;

                                            return (
                                                <Autocomplete
                                                    options={locationOptions}
                                                    getOptionLabel={option => option.name}
                                                    value={selectedLocation}
                                                    onChange={(_, newValue) => {
                                                        const newLocationId = newValue?.id ?? '';
                                                        setFieldValue('locationId', newLocationId);
                                                        // For adjustment mode, update quantity to current value at new location
                                                        if (isAdjustment && newLocationId) {
                                                            setFieldValue('quantity', getAvailableQuantity(newLocationId));
                                                        }
                                                    }}
                                                    onBlur={handleBlur}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            label="Location"
                                                            required
                                                            error={meta.touched && Boolean(meta.error)}
                                                            helperText={meta.touched && meta.error}
                                                        />
                                                    )}
                                                    fullWidth
                                                />
                                            );
                                        }}
                                    </Field>

                                    {isAdjustment && (
                                        <Typography variant="body2" color="text.secondary">
                                            Current quantity at this location: <strong>{availableQuantity}</strong>
                                        </Typography>
                                    )}

                                    <Field name="quantity">
                                        {({ field, meta }: any) => (
                                            <TextField
                                                {...field}
                                                label={isAdjustment ? 'New total quantity' : 'Quantity'}
                                                type="number"
                                                fullWidth
                                                required
                                                error={meta.touched && Boolean(meta.error)}
                                                helperText={meta.touched && meta.error}
                                                inputProps={{ min: isAdjustment ? 0 : 1, step: 1 }}
                                            />
                                        )}
                                    </Field>

                                    {isAdjustment && !adjustmentUnchanged && (
                                        <Alert severity={adjustmentDelta > 0 ? 'success' : 'warning'}>
                                            {adjustmentDelta > 0
                                                ? `This will increase inventory by ${adjustmentDelta}`
                                                : `This will decrease inventory by ${Math.abs(adjustmentDelta)}`}
                                        </Alert>
                                    )}

                                    <Field name="note">
                                        {({ field, meta }: any) => (
                                            <TextField
                                                {...field}
                                                label="Note (optional)"
                                                placeholder={config.notePlaceholder}
                                                multiline
                                                rows={3}
                                                fullWidth
                                                error={meta.touched && Boolean(meta.error)}
                                                helperText={meta.touched && meta.error}
                                                inputProps={{ maxLength: 500 }}
                                            />
                                        )}
                                    </Field>

                                    {error && <Alert severity="error">{error}</Alert>}
                                </Stack>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={onClose} disabled={isSubmitting}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" color={config.color} disabled={!canSubmit}>
                                    {isSubmitting ? 'Recording...' : isAdjustment ? 'Adjust' : 'Record'}
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
}
